import styled from "styled-components";

import Search from "../icons/Search";
import { TriggerButton as OverflowMenuButton } from "../OverflowMenu";

import logo from "../icons/np-logo-symbol.png";

import {
    colors,
    Grid,
    Row,
    Col,
    Section,
    Flexbox,
    A,
    mobileHeaderHeight,
    desktopHeaderHeight,
    breakpoints,
    zIndexTop,
    easingSeconds,
    focusBorder,
    NewBlackButton
} from "../shared";

// this is a container that will show while the header loads; it will either have the black background from the hero or the white bg that the regular header has. this is so that the loading icon does not show on a white background and then replace it with a header that has a dark background:
export const LoadingContainer = styled.div`
    ${({ heroSearchBgColor }) => `
        background-color: ${
            heroSearchBgColor ? heroSearchBgColor : colors.whiteDuplicate
        }
    `}
`;
//

export const Logo = styled.img.attrs({ src: logo })`
    width: 2.375rem;
    transition: opacity ${easingSeconds};

    &:hover {
        opacity: 0.6;
    }
`;

export const HeaderLogoLink = styled(A)`
    svg {
        transition: opacity ${easingSeconds};
    }
    &:hover svg {
        opacity: 0.6;
    }

    /* from mobile to desktop breakpoint: */
    @media (min-width: ${breakpoints.sm}rem) and (max-width: ${breakpoints.beforeDesktop}rem) {
        ${({ isHeroSearchV2 }) =>
            isHeroSearchV2 &&
            `
            height: 0.625rem; /* 10px */
            margin-top: -0.125rem; /* -2px */
        `}
        svg {
            margin-right: 1.25rem; /* 20px */
        }
    }
`;

// search
export const SearchInput = styled.input.attrs({
    type: "search"
})`
    width: 100%;
    border: 3px solid ${colors.emptySearchBG};
    transition: all ${easingSeconds};
    border-radius: 0;
    -webkit-appearance: none !important;
    font-family: inherit;
    color: ${colors.black};
    font-size: 1rem; /* 16px to avoid zoom on mobile devices */
    padding: 0.875rem 1rem 0.875rem 2.625rem;
    box-sizing: border-box !important; /* overrides main.css */

    @media (max-width: ${breakpoints.beforeTablet}rem) {
        padding: 0.375rem 0.625rem 0.375rem 3.125rem;
        /* ^^ 6px 10px 6px 50px - in the design, placeholder text is ~53px to the right of left side of input, making space for mobile search type toggle; then we subtract 3px for input border.  */
    }

    @media (min-width: ${breakpoints.md}rem) {
        /* padding for search input paired with toggle; more padding on the left so we make space for the toggle & on the right so we make space for the submit button */
        padding: 0.875rem 2.5rem 0.875rem 9rem; /* 14px 40px 14px 144px */
        height: 2.625rem; /* 42px - set height to prevent variable height on different browsers */
    }

    &::placeholder {
        opacity: 1;
        color: ${colors.brownishGreyTwo};
    }

    &:hover {
        border-color: ${colors.brownishGreyTwo};
    }

    &:focus {
        ${focusBorder};
        outline: none;
        &::placeholder {
            color: transparent;
            /* hides placeholder text on input focus */
        }
    }
`;

export const SearchWrapper = styled.div`
    position: relative;
`;

// non-mobile content
export const NonMobileTopRow = styled(Row)``;

export const MainLinksCol = styled(Col)`
    transition: opacity ${easingSeconds};
    [aria-hidden="true"] {
        display: none;
    }
`;

export const ProfileMenuCol = styled(Col)`
    transition: margin-top ${easingSeconds};
`;

export const SearchContainerRow = styled(Row)`
    transition: margin ${easingSeconds};

    /* prettier-ignore */
    ${Col}:nth-child(2) {	
        min-height: 42px;	
    }
`;

export const TextLinkGrid = styled(Grid)`
    overflow-x: visible;
`;

export const Link = styled(A)`
    font-size: 0.875rem;

    @media (max-width: ${breakpoints.beforeTablet}rem) {
        font-size: 0.75rem;
    }
`;

export const MainLinks = styled(Flexbox)`
    ${Link} {
        &:not(:last-of-type) {
            margin-right: 1rem;
        }
    }

    ${OverflowMenuButton} {
        margin-left: 1.25rem;
    }
`;

export const NonMobileContentWrapper = styled.div`
    ${NonMobileTopRow} {
        margin-bottom: 1.625rem;
        height: 24px;
    }

    ${Logo} {
        width: 5.25rem;
    }

    ${SearchWrapper} {
        width: 100%;
    }

    @media (max-width: ${breakpoints.beforeTablet}rem) {
        display: none;
    }
`;

// mobile content
export const LogoAndSearch = styled(Flexbox)`
    ${Logo} {
        margin-right: 1.25rem;
    }

    ${SearchWrapper} {
        flex: 1;
    }
`;

export const MenuAndAvatar = styled(Flexbox)`
    ${OverflowMenuButton} {
        ${({ loggedOut }) =>
            loggedOut ? "margin-left: 0.75rem" : "margin-right: 0.9375rem"}
    }

    /* up to Tablet size: */
    @media (max-width: ${breakpoints.beforeTablet}rem) {
        margin-top: -2px;
    }
`;

export const HeaderRow = styled(Row)``;

export const MobileContentWrapper = styled(Grid)`
    display: none;

    @media (max-width: ${breakpoints.beforeTablet}rem) {
        display: block;
        overflow-x: visible;

        ${HeaderRow} {
            height: 2.313rem; /* 37px - prevents jumping of header when scrolling, especially in terms of hero to header search input */
        }
    }
`;

// site header
export const StyledSiteHeader = styled(Section)`
    padding-top: 0.9375rem;
    padding-bottom: 0.9375rem;

    ${({ transparent }) =>
        transparent && "background-color: transparent; border-bottom: 0"};

    ${({ heroSearchBgColor }) =>
        `background-color: ${
            heroSearchBgColor ? heroSearchBgColor : colors.whiteDuplicate
        }; border-bottom: 0;`};


    ${({ transparent }) =>
        !transparent &&
        `background-color: ${colors.whiteDuplicate}; border-bottom: 1px solid #d8d8d8;`}

    min-height: ${mobileHeaderHeight}px;
    position: fixed;
    z-index: ${zIndexTop};
    transition: background ${easingSeconds};

    ${Link} {
        transition: all ${easingSeconds};
        color: ${({ transparent, isHeroSearchV2 }) =>
            transparent || isHeroSearchV2
                ? colors.whiteDuplicate
                : colors.brownishGreyTwo};
        text-decoration: none;

        &:hover {
            color: ${({ transparent, isHeroSearchV2 }) =>
                transparent
                    ? colors.dark
                    : isHeroSearchV2
                    ? colors.veryLightPinkTwo
                    : colors.black};
            text-decoration: underline;
        }
    }

    /* for new hero search/navbar combo; 
    these styles will be cleaned up significantly when we refactor the header animations/functionality: */
    ${({ isHeroSearchV2, heroSearchBgColor }) =>
        isHeroSearchV2 &&
        `
        background-color: ${
            heroSearchBgColor ? heroSearchBgColor : colors.whiteDuplicate
        };
        border-bottom: 0;

        ${Link} {
            color: ${colors.whiteDuplicate};
        }
    `}
    //
    
    
    /* Tablet + */
    @media (min-width: ${breakpoints.md}rem) {
        padding-top: 2rem;
        padding-bottom: 1.625rem;
        height: auto;
        max-height: ${desktopHeaderHeight}px;

        ${({ isHeroSearchV2 }) =>
            isHeroSearchV2 &&
            `
                height: ${desktopHeaderHeight}px;
                
                &.scrolledDown {
                    height: 5.938rem; /* 95px */
                }

                &.scrolledUp {
                    height: ${desktopHeaderHeight}px;
                }
        `}
    }
        
    ${({ isHeroSearchV2 }) =>
        isHeroSearchV2 &&
        `
        /* changes to header UI when scrolling down the page: */
        &.scrolledDown {
            /* SiteHeader itself: */
            border-bottom: 1px solid #d8d8d8;
            /* TODO: add heroSearchBgColor */

            /* overflow menu: */
            ${OverflowMenuButton} path {
                fill: ${colors.brownishGrey} !important;
                /* do not do this with important; need to figure out how to make TriggerButton.js work w/o important */
            }

            /* NP Logo: */
            a svg path {
                fill: ${colors.black};
            }
        }

        /* changes to header UI when scrolled to top of page: */
        &.scrolledUp {
            /* SiteHeader itself: */
            border-bottom: 0;
            /* TODO: add heroSearchBgColor */

            /* NP logo */
            a svg path {
                fill: ${colors.whiteDuplicate};
            }
        }

        /* when scrolling down the page, hide hero search & show header search */
        .scrolledDownSearch {
            form {
                display: block;
            }

            .mobile-toggle-dropdown {
                display: block;
            }
        }

        /* when scrolling up the page, show hero search & hide header search */
        .scrolledUpSearch form,
        .MuiToggleButtonGroup-root,
        .mobile-toggle-dropdown {
            display: none;
        }
    `}
`;

// new search button:
export const SubmitButton = styled(NewBlackButton)`
    border-radius: 0;
    position: absolute;
    top: 0.1875rem; /* 3px */
    right: 0.1875rem; /* 3px */
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
        fill: ${colors.emptySearchBG};
        width: 1rem; /* 16px */
        height: 1rem; /* 16px */
    }

    /* styles for hero: */
    #hero-search-wrapper & {
        height: 3.875rem; /* 62px */
        min-width: 3.875rem; /* 62px */
    }

    /* styles for header: */
    #site-header & {
        height: 2.25rem; /* 36px */
        min-width: 2.25rem; /* 36px */
        width: 2.25rem; /* 36px */
    }

    /* Hide button on mobile devices: */
    @media (max-width: ${breakpoints.beforeTablet}rem) {
        display: none;
    }

    /* CSS specific to iOS devices: https://stackoverflow.com/a/47818418 */
    @supports (-webkit-touch-callout: none) {
        right: 0.125rem; /* 2px */
    }
`;
